import { Translation } from 'cng-common/features/translations'
import { ModalDialog } from 'core-components/library/modalDialog'
import { BaseCrud } from 'core-components/types'
import { Partner360FontProvider } from 'partner360/components/typography/Partner360FontProvider'
import { KeyInitiative } from 'partner360/entities/keyInitiatives'

import { MDF_ACTIVITIES_TRANSLATION_KEYS } from '../../configs/translations'
import { useIsMdfActivityReadOnly } from '../../hooks/useIsMdfActivityReadOnly'
import { IMdfActivity, IMdfActivityWithBusinessPriorities } from '../../model/types'

import {
  IMdfActivityDetailsContentProps,
  MdfActivityDetailsContent,
} from './components/mdfActivityDetailsContent/MdfActivityDetailsContent'
import { MdfActivityDetailsFooter } from './components/mdfActivityDetailsFooter/MdfActivityDetailsFooter'

import styles from './MdfActivityDetailsModal.module.scss'

const KEYS = MDF_ACTIVITIES_TRANSLATION_KEYS.detailsModal

export interface IMdfActivityDetailsModalProps {
  show: boolean
  activity: IMdfActivityWithBusinessPriorities | null
  /** Key initiative linked to the activity (resolved by `keyInitiativeId`), or null when unlinked. */
  initiative?: KeyInitiative | null
  /**
   * CRUD source of available P360 key initiatives for the linked-initiative dropdown.
   */
  initiativesSource: BaseCrud<any>
  /**
   * Lookup of P360 key initiative id → name, used to resolve suggestion labels.
   */
  initiativesById: Map<number, string>
  /** Explicit editor year for consumers whose year is independent from the P360 tabs. */
  fYear?: number
  /** Focus the Outcome item editor when opened via the outcome button. */
  focusOutcome?: boolean
  onLink: (activity: IMdfActivity, keyInitiativeId: number | null) => void
  onCreateInitiative: (activity: IMdfActivity, value: string) => void
  onSaved?: () => void
  onClose: () => void
}

export const MdfActivityDetailsModal = ({
  show,
  activity,
  initiative,
  initiativesSource,
  initiativesById,
  fYear,
  focusOutcome,
  onLink,
  onCreateInitiative,
  onSaved,
  onClose,
}: IMdfActivityDetailsModalProps) => {
  const isReadOnly = useIsMdfActivityReadOnly(fYear)

  return show ? (
    <Partner360FontProvider>
      <ModalDialog<IMdfActivityDetailsContentProps>
        show={show}
        size="lg"
        classes={{ window: styles.window }}
        header={() => (
          <div className={styles.header}>
            <span className={styles.eyebrow}>
              <Translation text={KEYS.eyebrow} span />
            </span>
            <span className={styles.title}>{activity?.activityName ?? ''}</span>
          </div>
        )}
        content={MdfActivityDetailsContent}
        contentProps={{
          activity,
          initiative,
          initiativesSource,
          initiativesById,
          focusOutcome,
          isReadOnly,
          onLink,
          onCreateInitiative,
        }}
        footer={() => (
          <MdfActivityDetailsFooter
            activity={activity}
            fYear={fYear}
            isReadOnly={isReadOnly}
            onSaved={onSaved}
            onClose={onClose}
          />
        )}
        onClose={onClose}
      />
    </Partner360FontProvider>
  ) : null
}

MdfActivityDetailsModal.displayName = 'MdfActivityDetailsModal'
